import { ApiService } from './../../../../services/lsg.api.service';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  concatMap,
  delay,
  map,
  mergeMap,
  switchMap,
  takeWhile,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { EMPTY, forkJoin, interval, lastValueFrom, Observable, of } from 'rxjs';

import { Store } from '@ngrx/store';

import { findCountryByCodeOrName } from '../../../../data/countries.data';
import { getWebsiteMainAnalyticsActions } from './website-analytics.actions';
import { selectWebsiteState } from './website-analytics.selector';

@Injectable()
export class WebsiteAnalyticsEffects {
  isPolling = true;
  constructor(
    private actions$: Actions,
    private api: ApiService,
    private store: Store,
  ) {}

  // Analytics
  loadWebsiteAggregateAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getWebsiteMainAnalyticsActions.load),
      mergeMap(({ period }) => {
        const fetchPeriod = period || 'day';
        return forkJoin({
          aggregateAnalytics: this.api
            .getWebsiteAggregateAnalytics(fetchPeriod, () => {
              throw new Error('');
            })
            .pipe(catchError(() => of({ results: {} }))),
          bestCountry: this.api
            .getWebsiteBestTrafficCountry(fetchPeriod, () => {
              throw new Error('');
            })
            .pipe(catchError(() => of({ results: [] }))),
          realtimeVisitors: this.api
            .getWebsiteRealtimeCurrentVisitors(() => {
              throw new Error('');
            })
            .pipe(catchError(() => of(0))),
          topPages: this.api
            .getWebsiteBreakdown(
              'page',
              'event',
              10,
              fetchPeriod,
              'visitors,pageviews,bounce_rate',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          topSources: this.api
            .getWebsiteBreakdown(
              'source',
              'visit',
              10,
              fetchPeriod,
              'visitors,pageviews,bounce_rate,visit_duration',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          browsers: this.api
            .getWebsiteBreakdown(
              'browser',
              'visit',
              10,
              fetchPeriod,
              'visitors,pageviews',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          devices: this.api
            .getWebsiteBreakdown(
              'device',
              'visit',
              10,
              fetchPeriod,
              'visitors,pageviews',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          topCountries: this.api
            .getWebsiteBreakdown(
              'country',
              'visit',
              10,
              fetchPeriod,
              'visitors,pageviews',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          topRegions: this.api
            .getWebsiteBreakdown(
              'region',
              'visit',
              10,
              fetchPeriod,
              'visitors,pageviews',
              () => {
                throw new Error('');
              },
            )
            .pipe(catchError(() => of({ results: [] }))),
          timeSeries: this.api
            .getWebsiteTimeSeries(fetchPeriod, 'visitors', () => {
              throw new Error('');
            })
            .pipe(catchError(() => of({ results: [] }))),
        }).pipe(
          map(
            (response: {
              aggregateAnalytics: { results: any };
              bestCountry: { results: { country: string; visitors: number }[] };
              realtimeVisitors: number;
              topPages: { results: any[] };
              topSources: { results: any[] };
              browsers: { results: any[] };
              devices: { results: any[] };
              topCountries: { results: any[] };
              topRegions: { results: any[] };
              timeSeries: { results: any[] };
            }) => {
              const bestCountry = response.bestCountry?.results?.length
                ? {
                    ...response.bestCountry.results[0],
                    countryItem: findCountryByCodeOrName(
                      response.bestCountry.results[0].country,
                    ),
                  }
                : undefined;

              return getWebsiteMainAnalyticsActions.loadSuccess({
                stats: {
                  ...response.aggregateAnalytics.results,
                  bestCountry,
                  realtimeVisitors: response.realtimeVisitors,
                  topPages: (response.topPages?.results || []).map((r) => ({
                    ...r,
                    name: r.page,
                  })),
                  topSources: (response.topSources?.results || []).map((r) => ({
                    ...r,
                    name: r.source,
                  })),
                  browsers: (response.browsers?.results || []).map((r) => ({
                    ...r,
                    name: r.browser,
                  })),
                  devices: (response.devices?.results || []).map((r) => ({
                    ...r,
                    name: r.device,
                  })),
                  topCountries: (response.topCountries?.results || []).map(
                    (r) => ({
                      ...r,
                      name: r.country,
                      countryItem: findCountryByCodeOrName(r.country),
                    }),
                  ),
                  topRegions: (response.topRegions?.results || []).map((r) => ({
                    ...r,
                    name: r.region,
                  })),
                  timeSeries: response.timeSeries?.results || [],
                },
              });
            },
          ),
          catchError((error) =>
            of(getWebsiteMainAnalyticsActions.loadFailure({ error })),
          ),
        );
      }),
    ),
  );

  // Polling for Realtime Visitors
  startPollingRealtimeVisitors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getWebsiteMainAnalyticsActions.load), // Start polling after initial load success
      switchMap(() => this.pollRealtimeVisitors()), // Start long polling
    ),
  );

  // Helper function for long polling
  pollRealtimeVisitors(): Observable<any> {
    return this.api
      .getWebsiteRealtimeCurrentVisitors(() => {
        throw new Error('');
      })
      .pipe(
        withLatestFrom(this.store.select(selectWebsiteState)),
        // When the request completes, dispatch an action with the new data
        map(([realtimeVisitors, state]) => {
          const oldRealtimeVisitors = state.analytics?.realtimeVisitors;

          this.store.dispatch(
            getWebsiteMainAnalyticsActions.loadSuccess({
              stats: { realtimeVisitors },
            }),
          );

          if (realtimeVisitors !== oldRealtimeVisitors) {
            this.store.dispatch(
              getWebsiteMainAnalyticsActions.load({
                period: state.period,
                silent: true,
              }),
            );
          }
          return of(null);
        }),
        // Continue polling indefinitely with a delay
        catchError((error) => {
          console.error('Error getting real-time visitors:', error);
          this.isPolling = false;
          return of(getWebsiteMainAnalyticsActions.loadFailure({ error }));
        }),
        // Introduce a delay before the next polling request
        delay(12000), // 12 seconds delay
        // Repeat the polling request after the delay
        switchMap(() => {
          if (!this.isPolling) {
            return EMPTY;
          }
          return this.pollRealtimeVisitors();
        }),
      );
  }

  // Stop Polling
  stopPollingRealtimeVisitors() {
    this.isPolling = false;
  }
}
